import { bool, node, object, oneOf } from "prop-types";
import { createContext, useContext, useReducer } from "react";

export const defaultState = {
  cardType: false,
  paymentMethodToken: "",
  accountHolderName: "",
  last4: "",
  month: "",
  year: "",
  name: "",
  zip: "12345",
  nickname: "",
  isSavedPaymentMethod: true,
  isDefaultPaymentMethod: true,
  isTextToGiveEnabled: true,
  isSharedWithHousehold: true,
  submitting: false,
  deleting: false,
  ccReadyToSubmit: false,
  additionalInfoReadyToSubmit: true,
  canSaveCard: true,
  mustSaveCard: false,
  existingPaymentMethod: null,
  shouldRetokenize: false,
  showingNicknameCheckbox: null,
  isDeleted: false,
  replaceExistingCard: false,
  existingUserName: "",
  ach: {
    accountType: "checking", // checking or savings
    accountHolderType: "personal", // personal or business
    accountNumber: "",
    routingNumber: "",
    isEditing: false,
  },
  showUpdateUI: false,
};

function paymentMethodReducer(state, action) {
  let newState = { ...state };

  switch (action.type) {
    case "Update Value": {
      for (const key in action.values) {
        newState[key] = action.values[key];
      }
      return newState;
    }
    case "Toggle Value": {
      for (const key of action.values) {
        newState[key] = !newState[key];
      }
      return newState;
    }

    case "setExistingACHData": {
      return {
        ...state,
        ach: {
          ...state.ach,
          ...action.payload,
        },
      };
    }

    case "setACHState": {
      return {
        ...state,
        ach: {
          ...state.ach,
          ...action.payload,
        },
      };
    }

    case "Set Should Retokenize": {
      return {
        ...state,
        shouldRetokenize: action.payload,
      };
    }

    default:
      return newState;
  }
}

export const PaymentMethodState = createContext();

export function PaymentMethodProvider({
  children,
  initialState = {}, // used with testing
  existingPaymentMethod = {},
  initialACHAccountType,
  initialAccountHolderType,
  initialShouldRetokenize,
  ...props
}) {
  const [state, dispatch] = useReducer(paymentMethodReducer, {
    ...defaultState,
    ...initialState,
    ...existingPaymentMethod,
    replaceExistingCard:
      props.initialReplaceExistingCard ||
      existingPaymentMethod?.needsUpdate ||
      defaultState.replaceExistingCard,
    ach: {
      ...defaultState.ach,
      ...initialState.ach,
      accountType:
        initialACHAccountType ||
        existingPaymentMethod?.cardType ||
        defaultState.ach.accountType,
      accountHolderType:
        initialAccountHolderType?.toLowerCase() ||
        existingPaymentMethod?.accountHolderType?.toLowerCase() ||
        defaultState.ach.accountHolderType,
      isEditing: existingPaymentMethod?.needsUpdate,
    },
    isAlreadyExisting: Object.keys(existingPaymentMethod || {}).length > 0,
    shouldRetokenize: initialShouldRetokenize,
  });

  const setState = (values) => dispatch({ type: "Update Value", values });

  const setACHState = (payload) => {
    dispatch({ type: "setACHState", payload });
  };

  const toggleState = (values) => {
    if (typeof values === "string") values = [values];
    dispatch({ type: "Toggle Value", values });
  };

  const context = {
    ...state,
    dispatch,
    setState,
    setACHState,
    toggleState,
    savePaymentMethod: (overrideCcIsReady) => {
      let newState = { submitting: true };
      if (overrideCcIsReady) newState.ccReadyToSubmit = true;
      setState(newState);
    },
    deletePaymentMethod: () => {
      setState({ deleting: true });
    },
    ...props,
  };

  return (
    <PaymentMethodState.Provider value={context}>
      {children}
    </PaymentMethodState.Provider>
  );
}

PaymentMethodProvider.propTypes = {
  children: node,
  initialState: object,
  existingPaymentMethod: object,
  initialACHAccountType: oneOf(["checking", "savings", undefined]),
  initialAccountHolderType: oneOf(["personal", "business", undefined]),
  initialShouldRetokenize: bool,
};

export default function usePaymentMethodState() {
  const context = useContext(PaymentMethodState);

  if (!context) {
    throw new Error(
      "usePaymentMethodState must be used inside a PaymentMethodStateProvider"
    );
  }

  return context;
}
